import React from 'react'

interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'error' | 'warning' | 'info'
  /** Bold lead-in rendered before the message (e.g. "Session Expired"). */
  title?: string
}

// Color sets mirror the structural error states the login flow renders.
const VARIANT_STYLES = {
  error: 'bg-red-50 border-red-200',
  warning: 'bg-amber-50 border-amber-200',
  info: 'bg-blue-50 border-blue-200',
} as const

/**
 * Inline alert block shared by the login flow's structural states (invalid
 * credentials, suspended, SSO failure, session expired) and runtime errors
 * such as Google sign-in. Keep `role="alert"` and a stable `id` on each use.
 */
export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ variant = 'error', title, className = '', children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        role="alert"
        className={`p-4 rounded-lg border text-xs leading-relaxed ${VARIANT_STYLES[variant]} ${className}`}
        {...props}
      >
        <div className="flex items-center gap-2 mb-2">
          <img src="/error.svg" alt="error" className="w-6 h-6" />
          {title && <p className="font-semibold text-[16px]">{title}</p>}
        </div>
        <p className="ml-8 text-xs">{children}</p>
      </div>
    )
  },
)

Alert.displayName = 'Alert'
